/**
 * Find a Boutique — filters + Leaflet map for <find-a-boutique>
 */
(function () {
  const SELECTORS = {
    card: '[data-fab-card]',
    country: '[data-fab-country]',
    city: '[data-fab-city]',
    postal: '[data-fab-postal]',
    clear: '[data-fab-clear]',
    form: '[data-fab-form]',
    resultsCount: '[data-fab-results-count]',
    empty: '[data-fab-empty]',
    list: '[data-fab-list]',
    map: '[data-fab-map]',
    mapCanvas: '[data-fab-map-canvas]',
    mapPlaceholder: '[data-fab-map-placeholder]',
    locations: '[data-fab-locations]',
  };

  const DEFAULT_TILE_URL =
    'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
  const DEFAULT_ATTRIBUTION =
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

  function normalize(value) {
    return (value || '').trim().toLowerCase();
  }

  function readData(element, name) {
    return element.getAttribute('data-' + name) || '';
  }

  function parseLocations(root) {
    const node = root.querySelector(SELECTORS.locations);
    if (!node) return [];
    try {
      const data = JSON.parse(node.textContent || '[]');
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.warn('[find-a-boutique] Invalid locations JSON.', error);
      return [];
    }
  }

  function waitForLeaflet(maxMs) {
    return new Promise((resolve) => {
      if (window.L) {
        resolve(window.L);
        return;
      }
      const started = Date.now();
      const timer = window.setInterval(() => {
        if (window.L) {
          window.clearInterval(timer);
          resolve(window.L);
          return;
        }
        if (Date.now() - started >= maxMs) {
          window.clearInterval(timer);
          resolve(null);
        }
      }, 40);
    });
  }

  class FindABoutique extends HTMLElement {
    connectedCallback() {
      this.init();
    }

    async init() {
      if (this.hasAttribute('data-fab-initialized')) return;
      this.setAttribute('data-fab-initialized', '');

      this.cacheElements();
      if (!this.entries || !this.entries.length) return;

      this.buildCountryIndex();
      this.bindEvents();
      await this.initMap();
      this.applyFilters();
    }

    cacheElements() {
      this.form = this.querySelector(SELECTORS.form);
      this.countrySelect = this.querySelector(SELECTORS.country);
      this.citySelect = this.querySelector(SELECTORS.city);
      this.postalInput = this.querySelector(SELECTORS.postal);
      this.clearButton = this.querySelector(SELECTORS.clear);
      this.resultsCount = this.querySelector(SELECTORS.resultsCount);
      this.emptyState = this.querySelector(SELECTORS.empty);
      this.list = this.querySelector(SELECTORS.list);
      this.mapAside = this.querySelector(SELECTORS.map);
      this.mapCanvas = this.querySelector(SELECTORS.mapCanvas);
      this.mapPlaceholder = this.querySelector(SELECTORS.mapPlaceholder);
      this.cityDefaultLabel =
        (this.citySelect && this.citySelect.dataset.defaultLabel) || 'All cities';
      this.eventsBound = false;
      this.countryIndex = new Map();
      this.map = null;
      this.mapMarkers = new Map();
      this.mapReady = false;
      this.selectedLocationId = null;
      this.locations = parseLocations(this);

      this.entries = Array.from(this.querySelectorAll(SELECTORS.card)).map(
        (element) => {
          const lat = parseFloat(readData(element, 'lat'));
          const lng = parseFloat(readData(element, 'lng'));
          return {
            element,
            locationId: readData(element, 'location-id'),
            country: normalize(readData(element, 'country')),
            city: normalize(readData(element, 'city')),
            lat: Number.isFinite(lat) ? lat : null,
            lng: Number.isFinite(lng) ? lng : null,
            partner: readData(element, 'partner') === 'true',
            postalHaystack: [
              normalize(readData(element, 'postal')),
              normalize(readData(element, 'address-search')),
              normalize(readData(element, 'city')),
            ]
              .filter(Boolean)
              .join(' '),
          };
        }
      );
    }

    buildCountryIndex() {
      this.entries.forEach((entry) => {
        if (!entry.country) return;

        if (!this.countryIndex.has(entry.country)) {
          this.countryIndex.set(entry.country, {
            label: readData(entry.element, 'country').trim(),
            cities: new Map(),
          });
        }

        if (entry.city) {
          const bucket = this.countryIndex.get(entry.country);
          const cityLabel = readData(entry.element, 'city').trim();
          if (!bucket.cities.has(entry.city)) {
            bucket.cities.set(entry.city, cityLabel);
          }
        }
      });

      this.syncCountrySelectFromIndex();
    }

    syncCountrySelectFromIndex() {
      if (!this.countrySelect) return;
      if (this.countrySelect.options.length > 1) return;

      const fragment = document.createDocumentFragment();
      const countries = Array.from(this.countryIndex.values()).sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
      );

      countries.forEach((item) => {
        const option = document.createElement('option');
        option.value = item.label;
        option.textContent = item.label;
        fragment.appendChild(option);
      });

      this.countrySelect.appendChild(fragment);
    }

    populateCities(countryNormalized) {
      if (!this.citySelect) return;

      const fragment = document.createDocumentFragment();
      const defaultOption = document.createElement('option');
      defaultOption.value = '';
      defaultOption.textContent = this.cityDefaultLabel;
      fragment.appendChild(defaultOption);

      const bucket = countryNormalized
        ? this.countryIndex.get(countryNormalized)
        : null;
      const cities = bucket
        ? Array.from(bucket.cities.values()).sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: 'base' })
          )
        : [];

      cities.forEach((cityLabel) => {
        const option = document.createElement('option');
        option.value = cityLabel;
        option.textContent = cityLabel;
        fragment.appendChild(option);
      });

      this.citySelect.replaceChildren(fragment);
      this.citySelect.disabled = !countryNormalized || cities.length === 0;
      this.citySelect.setAttribute(
        'aria-disabled',
        this.citySelect.disabled ? 'true' : 'false'
      );
    }

    bindEvents() {
      if (this.eventsBound) return;
      this.eventsBound = true;

      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.applyFilters();
        });
      }

      if (this.countrySelect) {
        this.countrySelect.addEventListener('change', () => {
          this.onCountryChange();
        });
      }

      if (this.citySelect) {
        this.citySelect.addEventListener('change', () => {
          this.applyFilters();
        });
      }

      if (this.postalInput) {
        this.postalInput.addEventListener('input', () => {
          this.applyFilters();
        });
        this.postalInput.addEventListener('search', () => {
          this.applyFilters();
        });
      }

      if (this.clearButton) {
        this.clearButton.addEventListener('click', () => {
          this.clearFilters();
        });
      }

      this.entries.forEach((entry) => {
        if (!entry.locationId) return;

        entry.element.addEventListener('mouseenter', () => {
          if (this.selectedLocationId !== entry.locationId) {
            this.highlightMarker(entry.locationId, true);
          }
        });
        entry.element.addEventListener('mouseleave', () => {
          if (this.selectedLocationId !== entry.locationId) {
            this.highlightMarker(entry.locationId, false);
          }
        });
        entry.element.addEventListener('focusin', () => {
          if (this.selectedLocationId !== entry.locationId) {
            this.highlightMarker(entry.locationId, true);
          }
        });
        entry.element.addEventListener('focusout', () => {
          if (this.selectedLocationId !== entry.locationId) {
            this.highlightMarker(entry.locationId, false);
          }
        });

        if (entry.lat != null && entry.lng != null) {
          entry.element.addEventListener('click', (event) => {
            if (event.target.closest('a')) return;
            this.selectEntry(entry, { pan: true, openPopup: true, scroll: false });
          });
        }
      });
    }

    onCountryChange() {
      const countryNormalized = normalize(this.countrySelect?.value);
      this.populateCities(countryNormalized);

      if (this.citySelect) {
        this.citySelect.value = '';
      }

      this.applyFilters();
    }

    clearFilters() {
      if (this.countrySelect) {
        this.countrySelect.value = '';
      }

      if (this.citySelect) {
        this.citySelect.value = '';
        this.citySelect.disabled = true;
        this.citySelect.setAttribute('aria-disabled', 'true');
      }

      if (this.postalInput) {
        this.postalInput.value = '';
      }

      this.applyFilters();

      if (this.countrySelect) {
        this.countrySelect.focus();
      }
    }

    readFilters() {
      return {
        country: normalize(this.countrySelect?.value),
        city: normalize(this.citySelect?.value),
        postal: normalize(this.postalInput?.value),
      };
    }

    applyFilters() {
      const { country, city, postal } = this.readFilters();
      const hasFilter = Boolean(country || city || postal);
      let visible = 0;
      const visibleIds = new Set();

      this.entries.forEach((entry) => {
        const countryMatch = !country || entry.country === country;
        const cityMatch = !city || entry.city === city;
        const postalMatch = !postal || entry.postalHaystack.includes(postal);
        const show = countryMatch && cityMatch && postalMatch;

        entry.element.hidden = !show;
        if (show) {
          visible += 1;
          if (entry.locationId) visibleIds.add(entry.locationId);
        }
      });

      if (
        this.selectedLocationId &&
        !visibleIds.has(String(this.selectedLocationId))
      ) {
        this.clearSelectedEntry();
      }

      this.syncMapMarkers(visibleIds);
      this.updateUi(visible, hasFilter);
    }

    updateUi(visible, hasFilter) {
      if (this.resultsCount) {
        this.resultsCount.textContent = String(visible);
      }

      if (this.emptyState) {
        const showEmpty = visible === 0;
        this.emptyState.hidden = !showEmpty;

        if (showEmpty) {
          this.emptyState.setAttribute('role', 'status');
        } else {
          this.emptyState.removeAttribute('role');
        }
      }

      if (this.list) {
        this.list.setAttribute(
          'aria-label',
          visible === 1 ? '1 boutique' : `${visible} boutiques`
        );
      }

      if (this.clearButton) {
        this.clearButton.hidden = !hasFilter;
      }
    }

    async initMap() {
      if (!this.mapCanvas || !this.mapAside) return;

      const mappable = this.locations.filter(
        (item) =>
          Number.isFinite(Number(item.lat)) && Number.isFinite(Number(item.lng))
      );
      if (!mappable.length) return;

      const L = await waitForLeaflet(8000);
      if (!L) {
        console.warn('[find-a-boutique] Leaflet failed to load.');
        return;
      }

      const tileUrl =
        this.mapAside.getAttribute('data-map-tile-url') || DEFAULT_TILE_URL;
      const attribution =
        this.mapAside.getAttribute('data-map-attribution') || DEFAULT_ATTRIBUTION;

      this.map = L.map(this.mapCanvas, {
        zoomControl: true,
        scrollWheelZoom: false,
        attributionControl: true,
      });

      this.map.on('mouseover', () => {
        this.map.scrollWheelZoom.enable();
      });
      this.map.on('mouseout', () => {
        this.map.scrollWheelZoom.disable();
      });

      L.tileLayer(tileUrl, {
        attribution,
        subdomains: 'abcd',
        maxZoom: 19,
      }).addTo(this.map);

      const iconDefault = L.divIcon({
        className: 'fab-map-marker',
        html: '<span></span>',
        iconSize: [9, 9],
        iconAnchor: [4.5, 4.5],
      });

      const iconPartner = L.divIcon({
        className: 'fab-map-marker fab-map-marker--partner',
        html: '<span></span>',
        iconSize: [9, 9],
        iconAnchor: [4.5, 4.5],
      });

      const entryById = new Map(
        this.entries
          .filter((entry) => entry.locationId)
          .map((entry) => [entry.locationId, entry])
      );

      mappable.forEach((location) => {
        const lat = Number(location.lat);
        const lng = Number(location.lng);
        const entry = entryById.get(String(location.id));
        const isPartner = Boolean(location.partner || entry?.partner);
        const marker = L.marker([lat, lng], {
          icon: isPartner ? iconPartner : iconDefault,
          alt: location.name || 'Boutique',
        });

        const city = location.city || readData(entry?.element, 'city') || '';
        const name = location.name || '';
        marker.bindPopup(
          `<div class="fab-map-popup__name">${escapeHtml(name)}</div>` +
            (city
              ? `<p class="fab-map-popup__city">${escapeHtml(city)}</p>`
              : ''),
          { className: 'fab-map-popup', closeButton: false, offset: [0, -6] }
        );

        marker.on('click', () => {
          if (entry) {
            this.selectEntry(entry, { pan: false, openPopup: true, scroll: true });
          }
        });

        marker.addTo(this.map);
        this.mapMarkers.set(String(location.id), marker);
      });

      this.mapAside.classList.add('fab-map--ready');
      this.mapReady = true;

      const onResize = () => {
        if (this.map) this.map.invalidateSize();
      };
      window.addEventListener('resize', onResize);
      this._mapResizeHandler = onResize;

      requestAnimationFrame(() => {
        this.map.invalidateSize();
        this.fitMapToVisible(new Set(this.mapMarkers.keys()));
      });
    }

    syncMapMarkers(visibleIds) {
      if (!this.mapReady || !this.map) return;

      this.mapMarkers.forEach((marker, id) => {
        const el = marker.getElement();
        const show = visibleIds.has(id);
        if (show) {
          marker.addTo(this.map);
          if (el) el.classList.remove('fab-map-marker--hidden');
        } else if (el) {
          el.classList.add('fab-map-marker--hidden');
          marker.remove();
        }
      });

      this.fitMapToVisible(visibleIds);
    }

    fitMapToVisible(visibleIds) {
      if (!this.map || !visibleIds.size) return;

      const L = window.L;
      const bounds = [];
      visibleIds.forEach((id) => {
        const marker = this.mapMarkers.get(String(id));
        if (marker) bounds.push(marker.getLatLng());
      });

      if (!bounds.length) return;

      if (bounds.length === 1) {
        this.map.setView(bounds[0], 13, { animate: true });
        return;
      }

      this.map.fitBounds(L.latLngBounds(bounds), {
        padding: [48, 48],
        maxZoom: 12,
        animate: true,
      });
    }

    highlightMarker(locationId, active) {
      const marker = this.mapMarkers.get(String(locationId));
      const el = marker?.getElement();
      if (!el) return;
      el.classList.toggle('fab-map-marker--active', active);
    }

    clearSelectedEntry() {
      this.entries.forEach((entry) => {
        entry.element.classList.remove('fab-card--selected');
      });
      if (this.selectedLocationId) {
        const marker = this.mapMarkers.get(String(this.selectedLocationId));
        marker?.closePopup();
        this.highlightMarker(this.selectedLocationId, false);
      }
      this.selectedLocationId = null;
    }

    selectEntry(entry, options = {}) {
      const { pan = true, openPopup = true, scroll = false } = options;
      if (!entry?.locationId || entry.lat == null || entry.lng == null) return;

      this.clearSelectedEntry();
      this.selectedLocationId = entry.locationId;
      entry.element.classList.add('fab-card--selected');
      this.highlightMarker(entry.locationId, true);

      const marker = this.mapMarkers.get(String(entry.locationId));
      if (!this.map || !marker) return;

      if (!this.map.hasLayer(marker)) {
        marker.addTo(this.map);
        const el = marker.getElement();
        if (el) el.classList.remove('fab-map-marker--hidden');
      }

      if (pan) {
        this.map.setView([entry.lat, entry.lng], 15, { animate: true });
      }

      if (openPopup) {
        marker.openPopup();
      }

      if (scroll) {
        entry.element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      requestAnimationFrame(() => {
        this.map.invalidateSize();
      });
    }
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  if (!customElements.get('find-a-boutique')) {
    customElements.define('find-a-boutique', FindABoutique);
  }
})();
